/**
 * Fair Tracker's internal route table (spec § 4).
 *
 * The prototype used hash routes (`#/entry?supplier=id`); BeeNexa uses
 * BrowserRouter, so each becomes a path under /fair-tracker. Query parameters
 * are preserved exactly as the spec defines them, because screens read their
 * state from them (`?supplier=`, `?plan=`, the Suppliers filters, `?tab=`).
 *
 * Only the dashboard is `exact` — it sits at the app root, so without it every
 * other screen would also match it.
 *
 * NOTE ON COUNT: spec § 4 tabulates 16 routes while acceptance check #5 says
 * "all 15 routes render". 16 is what the table and the prototype actually
 * define, so 16 is what we build.
 */

import WelcomeScreen from './screens/WelcomeScreen';
import FairsScreen from './screens/FairsScreen';
import DashboardScreen from './screens/DashboardScreen';
import EntryScreen from './screens/EntryScreen';
import SuppliersScreen from './screens/SuppliersScreen';
import SupplierScreen from './screens/SupplierScreen';
import PlanScreen from './screens/PlanScreen';
import RevisitScreen from './screens/RevisitScreen';
import CardScreen from './screens/CardScreen';
import ChecklistScreen from './screens/ChecklistScreen';
import ShortlistScreen from './screens/ShortlistScreen';
import ReviewScreen from './screens/ReviewScreen';
import CompareScreen from './screens/CompareScreen';
import FollowupsScreen from './screens/FollowupsScreen';
import OutreachScreen from './screens/OutreachScreen';
import SettingsScreen from './screens/SettingsScreen';

/** Mount point inside BeeNexa. Every path below is relative to this. */
export const BASE = '/fair-tracker';

/** Build an absolute in-app path: p('/entry') → '/fair-tracker/entry'. */
export const p = (sub = '') => `${BASE}${sub}`;

/**
 * Order matters for <Switch> — most specific first. `chrome` controls which
 * shell pieces a screen gets:
 *   'none' — no chrome at all (unused: every screen needs a way out)
 *   'bare' — top bar only, no tab bar (the fairs hub, per spec § 4)
 *   'full' — top bar + tab bar (everything else)
 */
export const FT_ROUTES = [
  { key: 'welcome', path: p('/welcome'), component: WelcomeScreen, chrome: 'bare', title: 'Welcome' },
  { key: 'fairs', path: p('/fairs'), component: FairsScreen, chrome: 'bare', title: 'My Fairs' },
  { key: 'entry', path: p('/entry'), component: EntryScreen, chrome: 'full', title: 'Quick Entry' },
  { key: 'suppliers', path: p('/suppliers'), component: SuppliersScreen, chrome: 'full', title: 'My Suppliers' },
  { key: 'supplier', path: p('/s/:id'), component: SupplierScreen, chrome: 'full', title: 'Supplier' },
  { key: 'plan', path: p('/plan'), component: PlanScreen, chrome: 'full', title: 'My Fair Plan' },
  { key: 'revisit', path: p('/revisit'), component: RevisitScreen, chrome: 'full', title: 'Booths to Revisit' },
  { key: 'card', path: p('/card'), component: CardScreen, chrome: 'full', title: 'Show to supplier' },
  { key: 'checklist', path: p('/checklist'), component: ChecklistScreen, chrome: 'full', title: 'Pre-trip checklist' },
  { key: 'shortlist', path: p('/shortlist'), component: ShortlistScreen, chrome: 'full', title: 'Final Shortlist' },
  { key: 'review', path: p('/review/:id'), component: ReviewScreen, chrome: 'full', title: 'Final Review' },
  { key: 'compare', path: p('/compare'), component: CompareScreen, chrome: 'full', title: 'Compare' },
  { key: 'followups', path: p('/followups'), component: FollowupsScreen, chrome: 'full', title: 'Follow-ups' },
  { key: 'outreach', path: p('/outreach'), component: OutreachScreen, chrome: 'full', title: 'Message all finalists' },
  { key: 'settings', path: p('/settings'), component: SettingsScreen, chrome: 'full', title: 'Settings' },
  // Dashboard last and exact — it owns the app root.
  { key: 'dashboard', path: BASE, exact: true, component: DashboardScreen, chrome: 'full', title: 'Dashboard' },
];

/** The bottom tabs (spec § 4). `+Add` is the gold centre button. */
export const FT_TABS = [
  { key: 'home', label: 'Home', to: BASE, icon: 'Home' },
  { key: 'suppliers', label: 'Suppliers', to: p('/suppliers'), icon: 'Users' },
  { key: 'add', label: 'Add', to: p('/entry'), icon: 'Plus', accent: true },
  { key: 'shortlist', label: 'Shortlist', to: p('/shortlist'), icon: 'Star' },
  { key: 'followups', label: 'Follow-ups', to: p('/followups'), icon: 'Bell' },
];

/** Resolve the chrome mode for a pathname. Defaults to the dashboard's. */
export const chromeFor = (pathname) => {
  const hit = FT_ROUTES.find(
    (r) =>
      (r.exact ? pathname === r.path : pathname === r.path || pathname.startsWith(r.path + '/')) ||
      // ':id' routes: match the static prefix before the param.
      (r.path.includes('/:') && pathname.startsWith(r.path.split('/:')[0] + '/')),
  );
  return hit ? hit.chrome : 'full';
};

/** Which bottom tab is active, or null when none applies. */
export const activeTab = (pathname) => {
  if (pathname === BASE) return 'home';
  if (pathname.startsWith(p('/suppliers')) || pathname.startsWith(p('/s/'))) return 'suppliers';
  if (pathname.startsWith(p('/entry'))) return 'add';
  if (pathname.startsWith(p('/shortlist')) || pathname.startsWith(p('/review/'))) return 'shortlist';
  if (pathname.startsWith(p('/followups'))) return 'followups';
  return null;
};
